import { Router } from '@angular/router';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { PlaybookExecutionStatusSelectOptions } from 'src/app/models/selectOptions/executeCalculatedStatusSelectOptions.model';
import { ViolationStatusesSelectOptions } from 'src/app/models/selectOptions/violationStatusesSelectOptions.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { PlaybookExecutionTableColumns } from 'src/app/models/tableColumns/playbookExecutionsTableColumn.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { PlaybooksHttpServiceService } from 'src/app/services/httpServices/playbooksHttpService/playbooks-http-service.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { IntegrationModel } from '../../../api';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { TextFilter } from '../filtersTypes/textFilter.model';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';

export class PlaybookExecutionsFilters implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public integrationFilters: MultiSelectFilters;
  public executionStatusFilters: MultiSelectFilters;
  public violationStatusFilters: MultiSelectFilters;
  public playbookFilter: MultiSelectFilters;

  public lastExecutionSince: DateFilter;
  public lastExecutionUntil: DateFilter;
  public search: TextFilter;

  public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    undefined
  );
  private _defaultSortColumn: SortColumn | undefined = undefined;
  private readonly _isViolationStatusEnabled: boolean;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _playbooksHttpService: PlaybooksHttpServiceService,
    private readonly _logger: LoggerService,
    featureFlagsService: FeatureFlagsService,
    private readonly _permissionsService: PermissionsService
  ) {
    this._isViolationStatusEnabled = featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.ViolationStatusPlaybookExecHistory
    );
    this._initializeFilters();

    this._integrationsOnUpdate();
    this._playbooksOnUpdate();
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());

    this._initDefaultSortColumn();
  }

  public getDynamicFilters(): string[] {
    return [];
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.integrationFilters.getSelectedOptions(),
      ...this.executionStatusFilters.getSelectedOptions(),
      ...(this._isViolationStatusEnabled ? this.violationStatusFilters.getSelectedOptions() : []),
      ...this.playbookFilter.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      playbook: this.playbookFilter.getSelectedFiltersValues(),
      integration: this.integrationFilters.getSelectedFiltersValues(),
      executionStatus: this.executionStatusFilters.getSelectedFiltersValues(),
      violationStatus: this._isViolationStatusEnabled
        ? this.violationStatusFilters.getSelectedFiltersValues()
        : undefined,
      lastExecutionSince: this.lastExecutionSince.getFilterValue(),
      lastExecutionUntil: this.lastExecutionUntil.getFilterValue(),
      search: this.search.getFilterValue(),
    } as ISelectedFilters;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    values: ISelectOption[] | ISelectOption | Date | boolean | string | undefined
  ): void {
    let needUpdate = false;
    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.LastExecutionSince:
          this.lastExecutionSince.update(values as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.LastExecutionUntil:
          this.lastExecutionUntil.update(values as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.Search:
          this.search.update(values as string | undefined);
          needUpdate = true;
          break;
        default:
          break;
      }
    } else {
      if (values instanceof Array) {
        if (values.length == 0) {
          needUpdate = this._clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          values.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          needUpdate =
            this.playbookFilter.updateIfNeeded(optionsByCategory[FilterTypesEnum.Playbook] || []) || needUpdate;
          needUpdate =
            this.executionStatusFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ExecutionStatus] || []) ||
            needUpdate;
          if (this._isViolationStatusEnabled) {
            needUpdate =
              this.violationStatusFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ViolationStatus] || []) ||
              needUpdate;
          }
          needUpdate =
            this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
        }
      }
    }

    if (needUpdate) {
      this._handleSortedColumnOnFilter();
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: { q: filtersQuery },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach((option) => {
      if (option.filterType) {
        if (option.filterType in optionsIdsByCategory) {
          optionsIdsByCategory[option.filterType].add(option.id);
        } else {
          optionsIdsByCategory[option.filterType] = new Set([option.id]);
        }
      }
    });

    if (optionsIdsByCategory[FilterTypesEnum.ExecutionStatus]) {
      this.executionStatusFilters.updateIfNeeded(
        this.executionStatusFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.ExecutionStatus] &&
              !optionsIdsByCategory[FilterTypesEnum.ExecutionStatus].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Integration] &&
              !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Playbook]) {
      this.playbookFilter.updateIfNeeded(
        this.playbookFilter
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Playbook] &&
              !optionsIdsByCategory[FilterTypesEnum.Playbook].has(selectedOption.id)
          )
      );
    }

    if (this._isViolationStatusEnabled && optionsIdsByCategory[FilterTypesEnum.ViolationStatus]) {
      this.violationStatusFilters.updateIfNeeded(
        this.violationStatusFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.ViolationStatus] &&
              !optionsIdsByCategory[FilterTypesEnum.ViolationStatus].has(selectedOption.id)
          )
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: { q: filtersQuery },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();

      case FilterTypesEnum.Playbook:
        return this.playbookFilter.getOptions$();

      case FilterTypesEnum.ExecutionStatus:
        return this.executionStatusFilters.getOptions$();

      case FilterTypesEnum.ViolationStatus:
        if (!this._isViolationStatusEnabled) {
          const error = new Error(`PlaybookExecutionsFilters getFilterOptionsIfExist: ${filter} not enabled`);
          this._logger.error(error.message, { filter }, error);
          throw error;
        }
        return this.violationStatusFilters.getOptions$();

      default:
        const optionsError = new Error(`PlaybookExecutionsFilters getFilterOptionsIfExist: ${filter} not supported`);
        this._logger.error(optionsError.message, { filter }, optionsError);
        throw optionsError;
    }
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.ExecutionStatus:
        return this.executionStatusFilters.getSelectedFiltersIds();

      case FilterTypesEnum.ViolationStatus:
        if (!this._isViolationStatusEnabled) {
          const error = new Error(`PlaybookExecutionsFilters getFilterSelectedIfExist: ${filter} not enabled`);
          this._logger.error(error.message, { filter }, error);
          throw error;
        }
        return this.violationStatusFilters.getSelectedFiltersIds();

      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();

      case FilterTypesEnum.Playbook:
        return this.playbookFilter.getSelectedFiltersIds();

      default:
        const selectedError = new Error(`PlaybookExecutionsFilters getFilterSelectedIfExist: ${filter} not supported`);
        this._logger.error(selectedError.message, { filter }, selectedError);
        throw selectedError;
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values;

    if ('lastExecutionSince' in keyValueFilters) {
      this.lastExecutionSince.update(
        keyValueFilters['lastExecutionSince'] && moment(keyValueFilters['lastExecutionSince'][0], 'x').isValid()
          ? moment(keyValueFilters['lastExecutionSince'][0], 'x').toDate()
          : undefined
      );
      needUpdate = true;
    }

    if ('lastExecutionUntil' in keyValueFilters) {
      this.lastExecutionUntil.update(
        keyValueFilters['lastExecutionUntil'] && moment(keyValueFilters['lastExecutionUntil'][0], 'x').isValid()
          ? moment(keyValueFilters['lastExecutionUntil'][0], 'x').toDate()
          : undefined
      );
      needUpdate = true;
    }

    if ('search' in keyValueFilters || this.search.getFilterValue() !== undefined) {
      this.search.update(keyValueFilters['search'] ? keyValueFilters['search'][0] : undefined);
      needUpdate = true;
    }

    values = 'playbook' in keyValueFilters ? keyValueFilters['playbook'] : [];
    needUpdate = this.playbookFilter.updateByValuesIfNeeded(values) || needUpdate;

    values = 'executionStatus' in keyValueFilters ? keyValueFilters['executionStatus'] : [];
    needUpdate = this.executionStatusFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (this._isViolationStatusEnabled) {
      values = 'violationStatus' in keyValueFilters ? keyValueFilters['violationStatus'] : [];
      needUpdate = this.violationStatusFilters.updateByValuesIfNeeded(values) || needUpdate;
    }

    values = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: { q: filtersQuery },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public restoreSortColumnToDefaultIfNeeded() {
    this.sortedColumn$.next(this._defaultSortColumn);
  }

  private _clearAllMultiSelectFiltersIfNeeded(): boolean {
    let needUpdate = false;

    needUpdate = this.playbookFilter.updateIfNeeded([]) || needUpdate;
    needUpdate = this.integrationFilters.updateIfNeeded([]) || needUpdate;
    needUpdate = this.executionStatusFilters.updateIfNeeded([]) || needUpdate;
    if (this._isViolationStatusEnabled) {
      needUpdate = this.violationStatusFilters.updateIfNeeded([]) || needUpdate;
    }

    return needUpdate;
  }

  private _initializeFilters() {
    this.executionStatusFilters = new MultiSelectFilters(PlaybookExecutionStatusSelectOptions);
    this.violationStatusFilters = new MultiSelectFilters(
      this._isViolationStatusEnabled ? ViolationStatusesSelectOptions : []
    );
    this.playbookFilter = new MultiSelectFilters([]);
    this.integrationFilters = new MultiSelectFilters([]);
    this.lastExecutionSince = new DateFilter();
    this.lastExecutionUntil = new DateFilter();
    this.search = new TextFilter();

    this._setDefaultLastExecutionSince();
    this._updateAllOptions();
  }

  private _setDefaultLastExecutionSince() {
    const threeMonthsAgo = moment().subtract(3, 'months').startOf('day').toDate();
    this.lastExecutionSince.update(threeMonthsAgo);
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for playbook viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.PLAYBOOK_VIEWER
      );

      const integrationsOptionsMap: ISelectOption[] = [];

      filteredIntegrations.forEach((integration) => {
        integrationsOptionsMap.push({
          id: integration.id,
          value: integration.id,
          displayName: integration.name,
          filterType: FilterTypesEnum.Integration,
          categoryName: FilterTypesEnum.Integration,
          withoutBeautifyDisplayName: true,
        } as ISelectOption);
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));

      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  private _playbooksOnUpdate() {
    this._playbooksHttpService.getPlaybooks().subscribe((playbooks) => {
      const playbooksOptionsMap: ISelectOption[] = playbooks.map(
        (playbook) =>
          ({
            id: playbook.id,
            value: playbook.id,
            displayName: playbook.name,
            filterType: FilterTypesEnum.Playbook,
            categoryName: FilterTypesEnum.Playbook,
            withoutBeautifyDisplayName: true,
          }) as ISelectOption
      );

      const shouldUpdateSelectedOptions = this.playbookFilter.updateOptions(playbooksOptionsMap);

      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.playbookFilter.getOptions$().value,
      ...this.executionStatusFilters.getOptions$().value,
      ...(this._isViolationStatusEnabled ? this.violationStatusFilters.getOptions$().value : []),
      // ...this.integrationFilters.getOptions$().value
    ]);
  }

  private _initDefaultSortColumn() {
    const defaultSortColumn = PlaybookExecutionTableColumns.find((column) => column.propertyKey === 'startTime');
    if (defaultSortColumn) {
      this._defaultSortColumn = {
        column: defaultSortColumn,
        isDesc: true,
      };
    }
  }

  private _handleSortedColumnOnFilter() {
    if (this.sortedColumn$.value === undefined) {
      this.restoreSortColumnToDefaultIfNeeded();
    }
  }
}
